import type { SeedPeriodDto, SeedScoreDto } from '../api/useMatchEvents';

// LSports play-period ids the per-half seed writes to.
export const FIRST_HALF = 10;
export const SECOND_HALF = 20;

/** '' → 0, else the numeric value (inputs are pre-filtered to digits). */
export const num = (v: string) => (v.trim() === '' ? 0 : Number(v));

/**
 * By-half submit eligibility: at least one SCORE field must be typed. A per-half
 * seed asserts a COMPLETE baseline (untyped scores → 0), so gating on a corner
 * alone would let a "fix the corners" click clobber the live score to 0-0. An
 * explicit 0-0 still works by typing a `0` into any score field. Mirrors global
 * mode, which likewise requires the scores (not the corners).
 */
export function byHalfHasScore(
  f: Pick<SeedFormFields, 'h1' | 'a1' | 'h2' | 'a2'>,
): boolean {
  return [f.h1, f.a1, f.h2, f.a2].some((v) => v !== '');
}

export interface SeedFormFields {
  byHalf: boolean;
  // Global (total) fields.
  home: string;
  away: string;
  homeCorners: string;
  awayCorners: string;
  // Per-half fields.
  h1: string;
  a1: string;
  h2: string;
  a2: string;
  ch1: string;
  ca1: string;
  ch2: string;
  ca2: string;
}

/**
 * Build the seed POST body from the raw form fields — the single source of truth
 * for how the "set score" control maps to the wire contract, unit-tested apart
 * from the (repo-wide broken) DOM-render infra.
 *
 * - Global mode: `{ homeScore, awayScore, [homeCorners], [awayCorners] }` — the
 *   MATCH TOTAL, corners only when typed.
 * - By-half mode: the exact 1H/2H breakdown in `periods` (so the engine seeds the
 *   ledger buckets directly, no absorption), with `homeScore/awayScore` the DERIVED
 *   sum. Corners are attached to the periods (and the total) only if any corner
 *   field was typed — so a score-only per-half seed stays score-only. NOTE: once
 *   ANY corner is typed the seed asserts a COMPLETE corners snapshot, so the other
 *   (untyped) corner fields are written as 0 rather than omitted (unlike global
 *   mode, which omits an untyped corner to leave the prior untouched) — a per-half
 *   seed is a full baseline, not a partial patch.
 */
export function buildSeedDto(f: SeedFormFields): SeedScoreDto {
  if (!f.byHalf) {
    const dto: SeedScoreDto = { homeScore: Number(f.home), awayScore: Number(f.away) };
    if (f.homeCorners !== '') dto.homeCorners = Number(f.homeCorners);
    if (f.awayCorners !== '') dto.awayCorners = Number(f.awayCorners);
    return dto;
  }

  const hasCorners =
    f.ch1 !== '' || f.ca1 !== '' || f.ch2 !== '' || f.ca2 !== '';

  const period = (
    p: number,
    hs: string,
    as: string,
    chs: string,
    cas: string,
  ): SeedPeriodDto => {
    const entry: SeedPeriodDto = { period: p, home: num(hs), away: num(as) };
    if (hasCorners) {
      entry.homeCorners = num(chs);
      entry.awayCorners = num(cas);
    }
    return entry;
  };

  const dto: SeedScoreDto = {
    homeScore: num(f.h1) + num(f.h2),
    awayScore: num(f.a1) + num(f.a2),
    periods: [
      period(FIRST_HALF, f.h1, f.a1, f.ch1, f.ca1),
      period(SECOND_HALF, f.h2, f.a2, f.ch2, f.ca2),
    ],
  };
  if (hasCorners) {
    dto.homeCorners = num(f.ch1) + num(f.ch2);
    dto.awayCorners = num(f.ca1) + num(f.ca2);
  }
  return dto;
}
